import React, { useEffect, useMemo, useRef, useState } from 'react';
import { routes } from '../../routes/routes';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import buyerModule from '../../api/modules/buyer.module';
import { track } from '../../analytics/track';
import { EVENTS } from '../../analytics/events';
import { showToast } from '../../utils/toast';
import { useFavourites } from '../../favourites/useFavourites';
import { CollectionView } from './Collection.view';

/**
 * `/collection`, the container. Owns the fetch and nothing else.
 *
 * SAME DATA AS THE DASHBOARD TAB, DELIBERATELY. This calls the same
 * `buyerModule.getSavedCards`, keys on the same `['saved-cards', …]` family and
 * invalidates the same family after an unsave, so removing a card here also
 * refreshes `BuyerDashboard/SavedTable` and the profile's saved panel. Two
 * screens reading one collection through two cache keys is how they drift into
 * disagreeing about what is in it.
 *
 * The page size is 24 rather than the table's 10 because this is a grid, and
 * 10 leaves a ragged half row under a pager whatever the column count is. 24
 * divides exactly by the 4 and 6 the grid runs at (`collection.css`), and
 * leaves one short row at the 5 it runs at between 1200 and 1424 — no count
 * squares with all three, and a short LAST row is what a real final page looks
 * like anyway. The differing `limit` puts it in its own cache entry, which is
 * correct — it is a different page of the same list, not a conflicting answer.
 */
const PAGE_SIZE = 24;

export default function Collection() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const { ids, isGuest, toggle, dropIds } = useFavourites();

  const queryKey = useMemo(
    () => ['saved-cards', { page: currentPage, limit: PAGE_SIZE }],
    [currentPage],
  );

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey,
    /* A guest has no session for `getSavedCards` to key on — that branch below
       is what serves them instead. */
    enabled: !isGuest,
    queryFn: async () => {
      const { success, data: response, error } = await buyerModule.getSavedCards(queryKey[1]);

      if (!success) {
        console.error('Error fetching the collection:', error);
        /* Thrown, not swallowed into an empty page. `SavedTable` returns an
           empty envelope here, which makes a failed read indistinguishable
           from an empty collection — and "your collection is empty" is a
           frightening thing to tell someone whose request merely failed. */
        throw new Error(error || 'Failed to fetch the collection');
      }

      return response || { favourites: [], page: 1, page_size: PAGE_SIZE, total_pages: 0 };
    },
    keepPreviousData: true,
  });

  /* A guest's list is ids; the rows come from the public bulk read. Signed in,
     the existing saved-cards query already returns full rows.

     NORMALISED HERE, NOT AT EACH READ SITE. `cards-by-ids/` (this endpoint)
     answers with catalog rows keyed by `id` (`catalog_card_payload`);
     `favourites/list/` (the signed-in query above) answers with rows keyed
     by `product` (`get_favourites`'s `"product": str(fav.card_id)`). The
     view and this container's own `handleRemove`/`onOpenCard` read
     `row.product` as the ONE id concept for a collection row -- that was
     already true for the signed-in shape, so guest rows are reshaped to
     match it right here, at the one place that already knows which endpoint
     answered, rather than a `row.product || row.id` fallback guessed at
     every use site. Verified live: a guest's row had no `product` key at
     all before this, so `row.product` read `undefined` -- "Removed" toast
     with nothing removed, and `/marketplace/card/undefined`. */
  /* THE GUEST'S PAGE, cut from the id list before anything is fetched.
     A guest used to get the whole collection in one `cards-by-ids/` call and
     no pager at all (`pageCount` was hardcoded to 0, and the view only draws a
     pager above 1), so there was no next page to go to. That call caps at
     `MAX_IDS = 100`, so a guest with more than a hundred saved cards silently
     lost the remainder as well.

     Paging the IDS rather than the rows is what fixes both: each request now
     asks for at most `PAGE_SIZE` of them, which is comfortably under that cap
     however long the list gets, and the signed-out collection pages the same
     way the signed-in one already did. */
  const guestPageIds = useMemo(
    () => ids.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [ids, currentPage],
  );

  /* DON'T STRAND THEM PAST THE END. Removing the last card on the last page
     leaves `currentPage` pointing beyond a collection that just got shorter:
     the grid comes up empty, and since `pageCount` has dropped with it the
     pager is not rendered either, so there is nothing left to click to get
     back. A single-page collection could never do this -- it is the paging
     that makes the state reachable, so the paging has to answer for it. */
  useEffect(() => {
    if (!isGuest) return;
    const lastPage = Math.max(1, Math.ceil(ids.length / PAGE_SIZE));
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [isGuest, ids.length, currentPage]);

  const guestQuery = useQuery({
    queryKey: ['guest-collection', guestPageIds.join(',')],
    enabled: isGuest && guestPageIds.length > 0,
    queryFn: async () => {
      const result = await buyerModule.getCardsByIds(guestPageIds);
      if (!result.success) throw new Error(result.error || 'Failed to load your collection');
      return (result.data || []).map((card) => ({ ...card, product: card.id }));
    },
    /* Removing one card changes the key (it's `ids.join(',')`), which without
       this would blank the grid to the skeleton for every remaining card
       while the fresh id list resolves. `keepPreviousData: true` on the
       sibling query above is the v4 spelling and does nothing under v5 --
       `useQuery` never reads that field, only the `keepPreviousData` helper
       passed through `placeholderData` -- so this uses the form that
       actually works rather than copying dead code. */
    placeholderData: keepPreviousData,
  });

  /* Guarded past `ids.length`, not just `guestQuery.data || []`: once the
     LAST saved card is removed, `ids` goes to `[]`, the query above goes
     `enabled: false` and never runs again -- so with nothing left to
     overwrite it, `placeholderData: keepPreviousData` would otherwise go on
     showing the just-removed card forever. An id list with nothing in it
     has nothing to be a placeholder FOR. */
  const rows = isGuest ? (ids.length > 0 ? guestQuery.data || [] : []) : data?.favourites || [];

  /* PRUNE IDS THE CATALOG NO LONGER RESOLVES. `cards-by-ids/` omits ids it
     cannot find rather than erroring, so a card deleted since it was
     favourited leaves an id in localStorage with no tile behind it: "3 cards"
     over two tiles, and a dead id the sign-in merge then has to deal with on
     every page load forever. Dropping it here is the only place that knows
     which ids the catalog actually answered for.

     Guarded on `isPlaceholderData`: while a fresh id list is in flight,
     `placeholderData: keepPreviousData` serves the PREVIOUS key's rows, and
     pruning against those would delete an id that was just added. */
  useEffect(() => {
    if (!isGuest || guestPageIds.length === 0) return;
    if (!guestQuery.isSuccess || guestQuery.isPlaceholderData || guestQuery.isFetching) return;
    const resolved = new Set((guestQuery.data || []).map((row) => String(row.product)));
    /* SCOPED TO THE PAGE THAT WAS ACTUALLY REQUESTED. This compared the
       response against the ENTIRE id list, which was right when the entire
       list was fetched. Now that only a page is asked for, every id on every
       other page is missing from the response too -- pruning against all of
       them would delete the rest of the collection off the device the moment
       a paged collection was opened. */
    const dead = guestPageIds.filter((id) => !resolved.has(String(id)));
    if (dead.length > 0) dropIds(dead);
  }, [
    isGuest,
    guestPageIds,
    dropIds,
    guestQuery.data,
    guestQuery.isSuccess,
    guestQuery.isPlaceholderData,
    guestQuery.isFetching,
  ]);

  /* Guest count is the WHOLE id list now, not the rows.

     It used to be `rows.length`, because the grid held the entire collection
     and a count that contradicted the grid under it was worse than no count.
     Paging breaks that identity in the ordinary case: `rows` is one page, so
     `rows.length` would say "24 cards" to someone holding thirty. The list is
     the only thing that knows the real size.

     The old worry does not go away, it just shrinks to where the signed-in
     side already lives: an unresolvable id on a page nobody has opened yet
     still counts, until visiting that page prunes it. The signed-in total has
     always been a server count the visible page cannot corroborate either. */
  const total = isGuest ? ids.length : data?.total ?? rows.length;
  const isLoading = isGuest ? guestQuery.isFetching && rows.length === 0 : isFetching && rows.length === 0;
  const hasError = isGuest ? guestQuery.isError : isError;

  /* Once per mount, and only once the first response has landed, so the count
     on the event is a real count and not the zero of an empty first render.
     Same event and same property the dashboard tab fires: this is a second
     view of the collection, not a second feature. Guest mode has no
     `total_pages` to wait on — an id list with nothing left in flight is that
     mode's "landed". */
  const viewedFiredRef = useRef(false);
  useEffect(() => {
    if (viewedFiredRef.current) return;
    if (isGuest) {
      if (ids.length > 0 && guestQuery.isPending) return;
    } else if (data?.total_pages === undefined) {
      return;
    }
    viewedFiredRef.current = true;
    track(EVENTS.COLLECTION_VIEWED, { item_count: total, surface: 'collection_page' });
  }, [data, total, isGuest, ids.length, guestQuery.isPending]);

  const handleRemove = async (row) => {
    if (!row) return;
    /* A guest's collection lives on this device — the same local toggle the
       browse grids and detail pages use, not the session-keyed endpoint. */
    if (isGuest) {
      await toggle(row.product);
      showToast.success('Removed from your collection');
      return;
    }
    try {
      /* `row.product` is the card lookup id the favourites endpoints key on,
         NOT `row.card_id`. Same argument `SavedTable` passes. */
      const res = await buyerModule.unsaveCard(row.product);
      if (res.success) {
        showToast.success('Removed from your collection');
        queryClient.invalidateQueries(['saved-cards']);
        queryClient.invalidateQueries(['saved-cards-summary']);
      } else {
        showToast.error(res.error || 'Could not remove that card');
      }
    } catch (err) {
      showToast.error('Something went wrong');
      console.error(err);
    }
  };

  return (
    <CollectionView
      rows={rows}
      loading={isLoading}
      error={hasError ? 'We could not reach your collection.' : null}
      onRetry={isGuest ? guestQuery.refetch : refetch}
      page={isGuest ? currentPage : data?.page || currentPage}
      pageCount={isGuest ? Math.ceil(ids.length / PAGE_SIZE) : data?.total_pages || 0}
      pageSize={isGuest ? PAGE_SIZE : data?.page_size || PAGE_SIZE}
      total={total}
      onPageChange={setCurrentPage}
      /* THE ORIGIN TRAVELS WITH THE CLICK, the same way `/search` hands it
         over. Without it the card page falls back to its breadcrumb parent and
         Back reads "Back to Marketplace" — so opening one of your own saved
         cards and pressing Back put you in the general marketplace, out of the
         collection entirely. */
      onOpenCard={(row) =>
        navigate(`/marketplace/card/${row.product}`, {
          state: { from: routes.COLLECTION, fromLabel: 'your collection' },
        })
      }
      onRemove={handleRemove}
      isGuest={isGuest}
    />
  );
}
